<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { CommerceMutation } from '@/mutations/commerce.mutations';
import { FileMutation } from '@/mutations/file.mutation';
const {
  handleCreateCommerce,
  handleCreateCommerceContact,
  handleCreateCommerceLogo,
} = CommerceMutation();
const { handleUploadFile } = FileMutation();

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports assets / refs / données
// -------------------------------
import {
  idToDelete,
  isStepTwoActivated,
  form_CommerceBody,
  form_CommerceBodyUpdate,
  fileInput,
  errorMessage,
  isImageUploadid,
} from '@/refs/commerce.refs';
import {
  content,
  globalHeaderTitleLayout,
  isGlobalError,
  restaurantId,
} from '@/refs/common.refs';
import { IconUploadCloud } from '@/assets/svg/common.svg';
import { file, previewUrl } from '@/refs/file.refs';
import { form_FilfeBodyAdd } from '../../refs/file.refs';
import type { IFileImage } from '@/types/file.types';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const commerceStore = useCommerceStore();
const dialogStore = useDialogStore();

// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  form_FilfeBodyAdd.designation = null;
  file.value = null;
  previewUrl.value = null;

  idToDelete.value = null;
  dialogStore.closeDialog();
};

/**
 * Ouvre automatiquement le sélecteur de fichiers du navigateur.
 *
 * Cette fonction simule un clic sur l’input de type "file" caché,
 * afin de permettre à l’utilisateur de sélectionner une image sans
 * avoir à interagir directement avec le champ invisible.
 *
 * @function triggerUpload
 * @returns {void} Ne retourne aucune valeur
 */
const triggerUpload = () => {
  fileInput.value?.click();
};

/**
 * Gère la sélection d’un fichier et génère un aperçu visuel du logo.
 *
 * - Récupère le fichier sélectionné par l’utilisateur
 * - Vérifie sa taille maximale (5 Mo)
 * - Si valide, génère une URL temporaire pour l’afficher en aperçu
 * - Si invalide, affiche un message d’erreur
 *
 * @function handleFileChange
 * @param {Event} event - Événement déclenché lors du changement de fichier (input type="file")
 * @returns {void} Ne retourne aucune valeur
 */
const handleFileChange = (event: Event) => {
  const selectedFile = (event.target as HTMLInputElement).files?.[0];
  if (!selectedFile) return;
  if (selectedFile.size > 5 * 1024 * 1024) {
    errorMessage.value = 'La taille maximale autorisée est de 5 Mo.';
    return;
  }
  file.value = selectedFile;
  previewUrl.value = URL.createObjectURL(selectedFile);
  errorMessage.value = null;
};

/**
 * Supprime le logo sélectionné ainsi que son aperçu visuel.
 *
 * - Réinitialise l’URL d’aperçu
 * - Vide le champ de fichier
 * - Permet à l’utilisateur de choisir à nouveau une image
 *
 * @function handleDelete
 * @returns {void} Ne retourne aucune valeur
 */
const handleDelete = () => {
  previewUrl.value = null;

  // Réinitialise le champ fichier
  if (fileInput.value) fileInput.value.value = '';
};

/**
 * Soumet le formulaire de création du fichier.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */
const handleSubmit = async () => {
  //   isLoaderLoginFormActivated.value = true;

  try {
    const body: IFileImage = {
      restaurantId: restaurantId.value,
      file: file.value,
      designation: form_FilfeBodyAdd.designation,
    };
    isLoaderLoginFormActivated.value = true;
    const res = await handleUploadFile(body);

    if (res) {
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
        handleClose();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="space-y-[16px]">
      <TextInput
        required="true"
        v-model="form_FilfeBodyAdd.designation"
        name="Entrez la designation de l'image"
        placeholder="Entrez la designation de l'image"
        type="text"
        id="Entrez la designation de l'image"
        label="Entrez la designation de l'image"
      />
    </div>

    <div class="mx-auto flex w-full max-w-sm flex-col items-center gap-4">
      <div
        v-if="!previewUrl"
        class="border-border-input flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-white px-10 py-8 text-center transition hover:bg-gray-50"
        @click="triggerUpload"
      >
        <div
          v-html="IconUploadCloud"
          class="flex items-center justify-center"
        />

        <div class="space-y-1 text-sm text-gray-600">
          <p>Format : <span class="font-medium">PNG, JPEG, JPG</span></p>
          <p>Taille max : <span class="font-medium">5 Mo</span></p>
          <p>
            Résolution conseillée :
            <span class="font-medium">500 × 500 px</span>
          </p>
        </div>

        <span
          class="cursor-pointer text-sm text-indigo-600 underline hover:text-indigo-800"
        >
          Charger un fichier
        </span>

        <input
          required
          ref="fileInput"
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          class="hidden"
          @change="handleFileChange"
        />
      </div>

      <div
        v-else
        class="border-border-input relative h-48 w-full overflow-hidden rounded-2xl border border-dashed bg-white shadow-md"
      >
        <img
          :src="previewUrl"
          alt="Aperçu du logo"
          class="h-full w-full object-cover"
        />

        <!-- Bouton supprimer -->
        <button
          @click="handleDelete"
          class="absolute top-2 right-2 rounded bg-red-100 px-2 py-1 text-xs text-red-600 transition hover:bg-red-600 hover:text-white"
        >
          Supprimer
        </button>
      </div>
      <p v-if="errorMessage" class="text-center text-sm text-red-600">
        {{ errorMessage }}
      </p>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="submit"
            :label="'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
